import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { User, Building2, Phone, MapPin, Globe, Lock, Loader2, Save, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_phone: "", city: "", website_url: "" });
  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", confirm: "" });

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setProfile(res.data);
      const client = res.data.client || {};
      setForm({
        company_name: client.company_name || "",
        contact_phone: client.contact_phone || client.phone || "",
        city: client.city || "",
        website_url: client.website_url || "",
      });
    } catch { toast.error("Erreur chargement profil"); }
    finally { setLoading(false); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, form);
      toast.success("Profil mis a jour");
      fetchProfile();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    setChangingPw(true);
    try {
      await axios.put(`${API}/auth/password?old_password=${encodeURIComponent(pwForm.old_password)}&new_password=${encodeURIComponent(pwForm.new_password)}`);
      toast.success("Mot de passe modifie");
      setPwForm({ old_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur changement mot de passe"); }
    finally { setChangingPw(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
      </div>
    );
  }

  const client = profile?.client;

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <User className="h-6 w-6 text-[#0099CC]" />
          Mon profil
        </h1>
        <p className="text-[#94A3B8] mt-1">Gerez vos informations personnelles</p>
      </div>

      {/* Account Info (read-only) */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <h2 className="text-white font-semibold mb-4">Informations du compte</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#0099CC]/10 flex items-center justify-center">
              <User className="h-5 w-5 text-[#0099CC]" />
            </div>
            <div>
              <p className="text-[#64748b] text-xs">Email</p>
              <p className="text-white text-sm">{profile?.email}</p>
            </div>
          </div>
          {client && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-[#64748b] text-xs">Entreprise</p>
                  <p className="text-white text-sm">{client.company_name}</p>
                </div>
              </div>
              {client.sector && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="text-[#64748b] text-xs">Secteur</p>
                    <p className="text-white text-sm">{client.sector}</p>
                  </div>
                </div>
              )}
              {client.plan && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <p className="text-[#64748b] text-xs">Plan</p>
                    <p className="text-white text-sm capitalize">{client.plan}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Profile */}
      {client && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h2 className="text-white font-semibold mb-4">Modifier mes informations</h2>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Nom de l'entreprise</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                <Input value={form.company_name} onChange={e => setForm(p => ({...p, company_name: e.target.value}))}
                  className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" data-testid="profile-company" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Telephone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                <Input value={form.contact_phone} onChange={e => setForm(p => ({...p, contact_phone: e.target.value}))}
                  className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" placeholder="06 12 34 56 78" data-testid="profile-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Ville</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))}
                  className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Paris" data-testid="profile-city" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Site web</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                <Input value={form.website_url} onChange={e => setForm(p => ({...p, website_url: e.target.value}))}
                  className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white" placeholder="https://monsite.com" data-testid="profile-website" />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="save-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </form>
        </div>
      )}

      {/* Change Password */}
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
        <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#F59E0B]" />
          Changer le mot de passe
        </h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Mot de passe actuel</Label>
            <Input type="password" value={pwForm.old_password} onChange={e => setPwForm(p => ({...p, old_password: e.target.value}))}
              className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="current-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Nouveau mot de passe</Label>
            <Input type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({...p, new_password: e.target.value}))}
              className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="new-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Confirmer le nouveau mot de passe</Label>
            <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({...p, confirm: e.target.value}))}
              className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="confirm-password" />
          </div>
          <Button type="submit" disabled={changingPw || !pwForm.old_password || !pwForm.new_password}
            variant="outline" className="border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10" data-testid="change-password-btn">
            {changingPw ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            Modifier le mot de passe
          </Button>
        </form>
      </div>
    </div>
  );
}
